import React from 'react';
import type { Expert } from './expert.type';
import { SkeletonCard } from '../../hooks/useSkeletionCard';
import { ExpertCarousel } from './ExpertCarousel';
import { ExpertCard } from './ExpertCard';

// 🚀 1. IMPORT THE NEW PRESENCE HOOK
import { useReadExpertActivity } from '../../hooks/mainExpertAvailabilityHooks/useReadExpertActivity';

interface BaseExpertsProps {
  experts: Expert[];
  title?: string;
  loading?: boolean;
  error?: boolean;
}

export const BaseExperts: React.FC<BaseExpertsProps> = ({ 
  experts, 
  title = "All Experts",
  loading = false,
  error = false 
}) => {

  // 🚀 2. EXTRACT THE CODES AND START TRACKING THEM
  const expertCodes = experts.map(exp => exp.expertCode);
  const livePresenceMap = useReadExpertActivity(expertCodes);

  if (loading) {
    return (
      <div className="mb-12 font-['Poppins']">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 pl-3 border-l-4 border-blue-600">{title}</h2>
          <button className="text-blue-600 font-semibold text-sm hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 opacity-50 cursor-not-allowed">
            View More 
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        <div className="flex gap-6 overflow-hidden">
          {[1, 2, 3, 4].map((key) => (
            <div key={key} className="shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)]">
              <SkeletonCard />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 🟢 FIXED: Removed the ErrorCard grid and replaced it with a simple text message.
  if (error || (!loading && experts.length === 0)) {
    return (
      <div className="mb-12 font-['Poppins']">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 pl-3 border-l-4 border-blue-600 mb-6">{title}</h2>
        <div className="w-full py-12 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
          <p className="text-gray-500 font-medium">No experts found.</p>
          <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters.</p>
        </div>
      </div>
    );
  }

  return (
    <ExpertCarousel 
      title={title} 
      experts={experts} 
      isCarousel={true} 
      autoPlay={false} 
      // 🚀 3. PASS THE LIVE STATUS TO THE CARD
  renderCard={(expert) => (
  <ExpertCard
    data={{
      ...expert,
      isAvailableChat:
        livePresenceMap[expert.expertCode]?.isAvailableChat ??
        expert.isAvailableChat,

      isAvailableCall:
        livePresenceMap[expert.expertCode]?.isAvailableCall ??
        expert.isAvailableCall,

      isAvailableVideoCall:
        livePresenceMap[expert.expertCode]?.isAvailableVideoCall ??
        expert.isAvailableVideoCall,
    }}
    liveStatus={livePresenceMap[expert.expertCode]?.status}
  />
)}
    />
  );
};